/**
 * Live throughput — is work actually moving, right now?
 *
 * The board could already show depth, pending, lag and liveness, and none of
 * them answer this. "The queue is deep" and "the queue is deep AND nothing is
 * draining it" render identically, and every incident in the 2026-08 audit was
 * the second one. A worker can hold its heartbeat, report healthy, and complete
 * nothing for eleven hours.
 *
 * The reading comes from each worker's own completed-work counter, incremented
 * only after a batch returns. Never from Redis `entries-read`: trimming a
 * capped stream rewrites that counter, and on 2026-08-02 it read 213/s against
 * a true 0.69/s — wrong in the direction that makes a stalled lane look busy.
 *
 * REFRESH IS THE SAMPLING ACTION. A rate needs two readings of a cumulative
 * counter, so the first press establishes a baseline and honestly shows "—";
 * the second reports the rate over the interval between them. That is also why
 * this panel does not cache — see `live()` in api/queries.
 */
import { useThroughput, useClassifierThroughput } from '../api/queries'
import { PanelState, Tile } from '../components/ui'
import { n } from '../components/format'

/** The two roles each tab is actually asked about, and what to call them. */
const COLLECTOR: Array<[string, string]> = [
  ['channel', 'channel processor'],
  ['video', 'video processor'],
]
const CLASSIFIER: Array<[string, string]> = [
  ['classify', 'channel classifier'],
  ['video-classify', 'video classifier'],
]

type Row = { total: number | null; per_s: number | null; per_min: number | null }

function Rate({ row }: { row: Row | undefined }) {
  // NULL IS NOT ZERO. Null means no baseline yet — first press after a deploy,
  // a recreated container, or a sample older than an hour. Zero means nothing
  // moved, which is alarming and actionable. Rendering null as 0/s would paint
  // a healthy fleet as dead on every rollout, and an operator who learns to
  // ignore a red tile has lost the tile.
  if (!row || row.per_s === null) {
    return <span className="q" title="press Refresh again to measure a rate">—</span>
  }
  const stalled = row.per_s === 0 && (row.total ?? 0) > 0
  return (
    <span style={stalled ? { color: 'var(--crisis)' } : undefined}>
      {row.per_s.toFixed(2)}<span className="q">/s</span>
    </span>
  )
}

function Body({
  rows, data, isLoading, error, onRefresh, fetching,
}: {
  rows: Array<[string, string]>
  // Explicitly `| undefined` rather than `?`: this project sets
  // exactOptionalPropertyTypes, so "may be absent" and "may be undefined" are
  // different types and react-query hands us the second.
  data: { roles: Record<string, Row>; generated_at: string } | undefined
  isLoading: boolean
  error: unknown
  onRefresh: () => void
  fetching: boolean
}) {
  return (
    <Tile
      title="Throughput"
      hint="completed work per second"
      action={
        <button className="btn" onClick={onRefresh} disabled={fetching}>
          {fetching ? 'reading…' : 'refresh'}
        </button>
      }
    >
      <PanelState isLoading={isLoading} error={error}>
        <table className="t">
          <thead>
            <tr>
              <th>processor</th>
              <th className="n">rate</th>
              <th className="n">per min</th>
              <th className="n">completed</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([role, label]) => {
              const row = data?.roles?.[role]
              return (
                <tr key={role}>
                  <td>{label}</td>
                  <td className="n"><Rate row={row} /></td>
                  <td className="n q">{row?.per_min === null || row?.per_min === undefined
                    ? '—' : n(Math.round(row.per_min))}</td>
                  {/* The cumulative total is what separates "never started"
                      from "stopped": a rate of — with a total of 0 is a role
                      that has not run, not one that has stalled. */}
                  <td className="n q">{n(row?.total ?? null)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </PanelState>
    </Tile>
  )
}

export function Throughput() {
  const { data, isLoading, error, refetch, isFetching } = useThroughput()
  return (
    <Body rows={COLLECTOR} data={data} isLoading={isLoading} error={error}
          onRefresh={() => refetch()} fetching={isFetching} />
  )
}

export function ClassifierThroughput() {
  const { data, isLoading, error, refetch, isFetching } = useClassifierThroughput()
  return (
    <Body rows={CLASSIFIER} data={data} isLoading={isLoading} error={error}
          onRefresh={() => refetch()} fetching={isFetching} />
  )
}
